<template>
    <div class="comments-tab">
        <div class="tab-header">
            <div class="tab-header-left">
                <h3 class="tab-title">Comments</h3>
            </div>
        </div>

        <div class="comments-tab__container">
            <!-- Comments List -->
            <div class="comments-tab__list">
                <template v-for="comment in filteredComments">
                    <div 
                        v-if="!comment.is_internal || $gate.isAdmin()"
                        :key="comment.id"
                        class="comments-tab__item"
                    >
                        <div class="comments-tab__item-header">
                            <div class="comments-tab__author">
                                <div v-if="comment.author.avatar" v-html="comment.author.avatar" class="comments-tab__avatar"></div>
                                <div v-else class="comments-tab__avatar-placeholder">
                                    <theme-icon icon="user" type="icons" classes="w-5 h-5" />
                                </div>
                                <div class="comments-tab__author-info">
                                    <div style="display: flex; align-items: center; gap: 0.5rem;">
                                        <span class="comments-tab__author-name">{{ comment.author.name }}</span>
                                        <theme-icon 
                                            v-if="comment.is_internal" 
                                            icon="lock" 
                                            type="icons" 
                                            classes="w-4 h-4"
                                            title="Internal Comment"
                                        />
                                    </div>
                                    <span class="comments-tab__timestamp">{{ comment.posted_at }}</span>
                                </div>
                            </div>
                            <div v-if="$gate.isAdmin() || comment.author.id == $gate.user.id" class="comments-tab__actions">
                                <themed-button
                                    variant="ghost"
                                    size="icon"
                                    @click="openEditComment(comment.id)"
                                >
                                    <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
                                </themed-button>
                                <themed-button
                                    variant="ghost"
                                    size="icon"
                                    @click="confirmDeleteComment(comment.id)"
                                >
                                    <theme-icon icon="trash" type="icons" classes="w-4 h-4" />
                                </themed-button>
                            </div>
                        </div>

                        <div class="comments-tab__content">{{ comment.comment }}</div>

                        <div v-if="comment.attachment" class="comments-tab__attachment">
                            <div class="comments-tab__attachment-icon" v-html="comment.attachment.icon"></div>
                            <div class="comments-tab__attachment-info">
                                <p class="comments-tab__attachment-name">{{ comment.attachment.name | truncate(65) }}</p>
                                <p v-if="comment.attachment.description" class="comments-tab__attachment-description">
                                    {{ comment.attachment.description }}
                                </p>
                            </div>
                            <div class="comments-tab__attachment-actions">
                                <a 
                                    v-if="comment.attachment.is_image"
                                    :href="`#lightbox-${comment.attachment.id}`"
                                    class="comments-tab__attachment-button"
                                    title="View Image"
                                >
                                    <theme-icon icon="view-icon" type="icons" classes="w-4 h-4" />
                                </a>
                                <a 
                                    v-else
                                    :href="comment.attachment.preview_url" 
                                    target="_blank"
                                    class="comments-tab__attachment-button"
                                    title="Preview"
                                >
                                    <theme-icon icon="view-icon" type="icons" classes="w-4 h-4" />
                                </a>
                                <a 
                                    :href="comment.attachment.download_url"
                                    class="comments-tab__attachment-button"
                                    title="Download"
                                >
                                    <theme-icon icon="download-icon" type="icons" classes="w-4 h-4" />
                                </a>
                                <a 
                                    v-if="comment.attachment.is_image"
                                    href="#_" 
                                    class="lightbox" 
                                    :id="`lightbox-${comment.attachment.id}`"
                                >
                                    <img :src="comment.attachment.view_url" :alt="comment.attachment.name">
                                </a>
                            </div>
                        </div>
                    </div>
                </template>

                <div v-if="filteredComments.length === 0" class="comments-tab__empty">
                    <p class="none-found">No Comments Found</p>
                </div>
            </div>

            <!-- New Comment Form -->
            <div class="comments-tab__form">
                <h3 class="comments-tab__form-title">Post New Comment</h3>

                <themed-textarea
                    v-model="newComment.comment"
                    placeholder="Enter your comment here..."
                    :rows="8"
                    class="comments-tab__textarea"
                />

                <div v-if="$gate.isAdmin()" class="comments-tab__internal-toggle">
                    <div class="comments-tab__internal-info">
                        <p class="comments-tab__internal-label">Internal Comment?</p>
                        <p class="comments-tab__internal-description">
                            If turned on, this comment will only be viewable by system administrators
                        </p>
                    </div>
                    <themed-toggle v-model="newComment.is_internal" />
                </div>

                <div class="comments-tab__attachment-section">
                    <h4 class="comments-tab__attachment-title">Add Attachment</h4>

                    <div class="comments-tab__attachment-selects">
                        <themed-form-field label="Category">
                            <themed-select
                                v-model="selectedCategory"
                                :options="categoryOptions"
                                @input="handleCategoryChange"
                            />
                        </themed-form-field>

                        <themed-form-field v-if="selectedCategory" label="Subcategory">
                            <themed-select
                                v-model="selectedSubcategory"
                                :options="subcategoryOptions"
                                @input="handleSubcategoryChange"
                            />
                        </themed-form-field>
                    </div>

                    <themed-form-field v-if="availableDocuments.length" label="Document">
                        <themed-select
                            v-model="newComment.attachment_id"
                            :options="documentOptions"
                        />
                    </themed-form-field>

                    <div v-if="selectedCategory && !availableDocuments.length" class="comments-tab__no-documents">
                        No Documents Found
                    </div>
                </div>

                <themed-button
                    variant="default"
                    @click="saveComment"
                    :disabled="!newComment.comment"
                    class="comments-tab__submit"
                >
                    Save Comment
                </themed-button>
            </div>
        </div>

        <!-- Edit Comment Modal -->
        <edit-comment-modal
            v-if="selectedCommentId"
            :comment-id="selectedCommentId"
            @comment-updated="commentUpdated"
        />

        <notifications classes="notification" position="bottom right" />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedButton from '../ThemedButton.vue';
import ThemedToggle from '../ThemedToggle.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedTextarea from '../form/ThemedTextarea.vue';
import ThemedFormField from '../form/ThemedFormField.vue';
import ThemeIcon from '../ThemeIcon.vue';
import EditCommentModal from './EditCommentModal.vue';

export default {
    name: 'CommentsV1',
    
    components: {
        ThemedButton,
        ThemedToggle,
        ThemedSelect,
        ThemedTextarea,
        ThemedFormField,
        ThemeIcon,
        EditCommentModal
    },

    props: {
        data: {
            type: [Array, Object],
            required: true
        },
        documentData: {
            type: [Array, Object],
            required: true
        },
        commentableId: {
            type: Number,
            required: true
        },
        commentableType: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            newComment: {
                commentable_id: this.commentableId,
                commentable_type: this.commentableType,
                author_id: window.user.id,
                comment: '',
                attachment_id: '',
                is_internal: false
            },
            selectedCommentId: null,
            selectedCategory: '',
            selectedSubcategory: ''
        };
    },

    computed: {
        ...mapState(['sites', 'comments', 'documents']),

        filteredComments() {
            return this.comments.filtered || [];
        },

        categoryOptions() {
            return [
                { value: '', label: 'Select Category' },
                ...this.documents.categories.map(cat => ({
                    value: cat.id,
                    label: cat.name
                }))
            ];
        },

        subcategoryOptions() {
            const subcategories = this.documents.subcategories.filter(
                sub => sub.category_id == this.selectedCategory
            );
            return [
                { value: '', label: 'Select Subcategory' },
                ...subcategories.map(sub => ({
                    value: sub.id,
                    label: sub.name
                }))
            ];
        },

        availableDocuments() {
            return this.documents.all.filter(file => {
                if (this.selectedSubcategory) {
                    return file.category_id == this.selectedCategory && 
                           file.subcategory_id == this.selectedSubcategory;
                } else {
                    return file.category_id == this.selectedCategory;
                }
            });
        },

        documentOptions() {
            return [
                { value: '', label: 'Select Document' },
                ...this.availableDocuments.map(doc => ({
                    value: doc.id,
                    label: doc.name
                }))
            ];
        }
    },

    mounted() {
        this.comments.all = this.data;
        this.comments.filtered = this.data;
        this.documents.all = this.documentData;
        this.documents.filtered = this.documentData;
        this.$store.dispatch('loadCategories');
    },

    methods: {
        openEditComment(commentId) {
            this.selectedCommentId = commentId;
            this.$modal.show('comment-edit');
        },

        handleCategoryChange() {
            this.selectedSubcategory = '';
            this.newComment.attachment_id = '';
        },

        handleSubcategoryChange() {
            this.newComment.attachment_id = '';
        },

        saveComment() {
            axios.post('/comments', this.newComment)
                .then(response => {
                    this.comments.filtered.unshift(response.data.comment);
                    this.sites.selectedSite.comments = this.comments.all;
                    this.resetForm();

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });
                })
                .catch(error => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response?.data?.message || 'Failed to save comment'
                    });
                });
        },

        confirmDeleteComment(commentId) {
            this.$swal({
                title: 'Are you sure?',
                text: "Are you sure you want to delete this comment?",
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.deleteComment(commentId);
                }
            });
        },

        deleteComment(commentId) {
            axios.delete('/comments/' + commentId)
                .then(response => {
                    const index = this.comments.filtered.findIndex(c => c.id === commentId);
                    if (index !== -1) {
                        this.comments.filtered.splice(index, 1);
                    }
                    
                    if (this.sites.selectedSite) {
                        this.sites.selectedSite.comments = this.comments.filtered;
                    }

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: 'Comment Deleted'
                    });
                })
                .catch(error => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response?.data?.message || 'Failed to delete comment'
                    });
                });
        },

        commentUpdated(commentData) {
            const index = this.comments.filtered.findIndex(c => c.id === commentData.id);
            if (index !== -1) {
                this.comments.filtered.splice(index, 1);
            }
            this.comments.filtered.unshift(commentData);
            
            if (this.sites.selectedSite) {
                this.sites.selectedSite.comments = this.comments.filtered;
            }
        },

        resetForm() {
            this.newComment.comment = '';
            this.newComment.attachment_id = '';
            this.newComment.is_internal = false;
            this.selectedCategory = '';
            this.selectedSubcategory = '';
        }
    }
};
</script>
